/**
 * 세션 관리 시스템
 * 사용자 인증 세션의 시작, 종료, 만료 체크를 담당
 * 보안 강화: 세션 데이터 암호화
 */

import { encryptSession, decryptSession } from '../services/webCryptoService';

class SessionManager {
  constructor() {
    this.sessionKey = 'newpass2_session';
    this.sessionTimeout = 4 * 60 * 60 * 1000; // 4시간 (밀리초)
    this.sessionEncryptionKey = 'newpass2_session_storage_key'; // 세션 전용 고정 키
  }

  /**
   * 새 세션 시작
   * @param {Object} user - Firebase 사용자 객체
   */
  async startSession(user) {
    // 기존 세션 확인 (중복 방지)
    const existingSession = await this.getCurrentSession();
    if (existingSession && existingSession.uid === user.uid) {
      // 같은 사용자의 기존 세션이 있으면 활동만 업데이트
      await this.updateActivity();
      return;
    }

    const sessionData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      startTime: Date.now(),
      lastActivity: Date.now(),
      expiresAt: Date.now() + this.sessionTimeout
    };

    try {
      // 세션 데이터 암호화 (세션 전용 키 사용)
      const encrypted = await encryptSession(
        JSON.stringify(sessionData),
        this.sessionEncryptionKey
      );
      localStorage.setItem(this.sessionKey, encrypted);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 세션 시작 (개발 모드)');
      }
    } catch (error) {
      console.error('❌ 세션 저장 실패:', error);
    }
  }

  /**
   * 세션 종료
   */
  async endSession() {
    const session = await this.getCurrentSession();
    
    try {
      localStorage.removeItem(this.sessionKey);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔓 세션 종료 (개발 모드)');
      }
    } catch (error) {
      console.error('❌ 세션 제거 실패:', error);
    }
  }

  /**
   * 현재 세션 정보 조회
   * @returns {Object|null} 세션 데이터 또는 null
   */
  async getCurrentSession() {
    try {
      const encryptedData = localStorage.getItem(this.sessionKey);
      if (!encryptedData) return null;
      
      const decrypted = await decryptSession(
        encryptedData,
        this.sessionEncryptionKey
      );
      
      return JSON.parse(decrypted);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('세션 조회 실패:', error);
      }
      // 세션 데이터 복호화 실패 시 세션 정리
      localStorage.removeItem(this.sessionKey);
      return null;
    }
  }

  /**
   * 세션 만료 여부 확인
   * @returns {boolean} 만료되었으면 true
   */
  async isSessionExpired() {
    const session = await this.getCurrentSession();
    if (!session) return true;

    const now = Date.now();
    return now > session.expiresAt;
  }

  /**
   * 세션 활동 시간 업데이트
   */
  async updateActivity() {
    const session = await this.getCurrentSession();
    if (session) {
      session.lastActivity = Date.now();
      
      try {
        const encrypted = await encryptSession(
          JSON.stringify(session),
          this.sessionEncryptionKey
        );
        localStorage.setItem(this.sessionKey, encrypted);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('세션 활동 업데이트 실패:', error);
        }
      }
    }
  }

  /**
   * 세션 연장
   */
  async extendSession() {
    const session = await this.getCurrentSession();
    if (session) {
      session.expiresAt = Date.now() + this.sessionTimeout;
      session.lastActivity = Date.now();
      
      try {
        const encrypted = await encryptSession(
          JSON.stringify(session),
          this.sessionEncryptionKey
        );
        localStorage.setItem(this.sessionKey, encrypted);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 세션 연장됨 (개발 모드)');
        }
      } catch (error) {
        console.error('❌ 세션 연장 실패:', error);
      }
    }
  }

  /**
   * 남은 세션 시간 (분)
   * @returns {number} 남은 시간 (분)
   */
  async getSessionTimeRemaining() {
    const session = await this.getCurrentSession();
    if (!session) return 0;

    const remaining = session.expiresAt - Date.now();
    return Math.max(0, Math.floor(remaining / (60 * 1000)));
  }

  /**
   * 세션 통계 정보
   * @returns {Object} 세션 통계
   */
  async getSessionStats() {
    const session = await this.getCurrentSession();
    if (!session) return null;

    const now = Date.now();
    const duration = now - session.startTime;
    const timeRemaining = await this.getSessionTimeRemaining();

    return {
      duration: Math.floor(duration / (60 * 1000)), // 분
      timeRemaining,
      isExpired: await this.isSessionExpired(),
      lastActivity: new Date(session.lastActivity).toLocaleTimeString()
    };
  }
}

// 싱글톤 인스턴스 생성
export const sessionManager = new SessionManager();

// 페이지 활동 감지하여 세션 업데이트
// if (typeof window !== 'undefined') {
//   // 마우스, 키보드, 스크롤 활동 감지
//   const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  
//   activities.forEach(activity => {
//     document.addEventListener(activity, () => {
//       sessionManager.updateActivity();
//     }, true);
//   });
// }
